import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { EMPTY, Subject, catchError, finalize, takeUntil, tap } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { INVENTORY_ITEM_TYPE_SERVICE } from 'src/app/core/enums/inventory-item-type.enum';
import { UnitOfMeasureLookup } from 'src/app/core/models/inventory/unit-of-measure-lookup.interface';
import { CompanySalesOrderDetails } from 'src/app/core/models/sales/company-sales-order-details.interface';
import { CompanySalesOrderLine } from 'src/app/core/models/sales/company-sales-order-line.interface';
import { CreateShipmentFormGroup } from 'src/app/core/models/sales/create-shipment-form-group.interface';
import { CreateShipmentFormModel } from 'src/app/core/models/sales/create-shipment-form.model';
import { CreateShipmentLineFormGroup } from 'src/app/core/models/sales/create-shipment-line-form-group.interface';
import { CreateShipmentLineFormModel } from 'src/app/core/models/sales/create-shipment-line-form.model';
import { CreateShipmentRequest } from 'src/app/core/models/sales/create-shipment-request.interface';
import { SalesOrderShipmentService } from 'src/app/core/services/sales-order-shipment.service';
import { SalesOrderInventoryItemOption } from '../../sales-order-overview.models';

interface CreateShipmentLineRowView {
  index: number;
  description: string;
  description2: string;
  unitLabel: string;
  remaining: number;
  maxLabel: string;
  /**
   * True when the source inventory item is of type `Service`. Service lines skip the
   * remaining-quantity validator (the backend ignores stock for Service entirely) and
   * the row UI shows an "Always available" badge instead of the "Max: N" hint.
   */
  isServiceLine: boolean;
}

@Component({
  selector: 'app-sales-order-create-shipment-modal',
  standalone: true,
  imports: [CommonModule, CoreModule, ReactiveFormsModule, TranslateModule],
  providers: [CreateShipmentFormModel, CreateShipmentLineFormModel],
  templateUrl: './sales-order-create-shipment-modal.component.html',
  styleUrl: './sales-order-create-shipment-modal.component.scss',
})
export class SalesOrderCreateShipmentModalComponent implements OnChanges, OnDestroy {
  readonly formModel = inject(CreateShipmentFormModel);
  private readonly shipmentService = inject(SalesOrderShipmentService);

  @Input({ required: true }) public companyId = '';
  @Input({ required: true }) public salesOrder: CompanySalesOrderDetails | null = null;
  @Input({ required: true }) public unitsOfMeasure: UnitOfMeasureLookup[] = [];
  /**
   * Inventory-item options resolved by the parent overview. Used to detect Service items
   * per line (the order line itself only carries `inventoryItemId`); when missing or
   * empty every line is treated as Product/Hybrid which preserves the legacy behavior.
   */
  @Input() public inventoryItemOptions: SalesOrderInventoryItemOption[] = [];
  @Input() public isOpen = false;
  // Resolved human-readable customer name (organization title or contact full name) computed by
  // the parent overview from `clientOptions`. The legacy `customerNumber` (e.g. "C-1234") stays
  // available as a fallback when the lookup misses (e.g. orphan reference, BC-imported order
  // without a matching CRM record).
  @Input() public customerName = '';

  @Output() public readonly closed = new EventEmitter<void>();
  @Output() public readonly shipmentCreated = new EventEmitter<void>();

  public readonly today = SalesOrderCreateShipmentModalComponent.formatToday();

  public lineRows: CreateShipmentLineRowView[] = [];
  public customerLabel = '';
  public salesOrderNumberLabel = '';
  public isSubmitting = false;
  public errorMessageKey = '';

  private readonly unsubscribe$ = new Subject<void>();

  public get form(): FormGroup<CreateShipmentFormGroup> {
    return this.formModel.form;
  }

  public get lineForms(): FormArray<FormGroup<CreateShipmentLineFormGroup>> {
    return this.formModel.lines;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isOpen'] ||
      changes['salesOrder'] ||
      changes['unitsOfMeasure'] ||
      changes['inventoryItemOptions'] ||
      changes['customerName']
    ) {
      if (this.isOpen) {
        this.resetFormFromOrder();
      }
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onCancel(): void {
    if (this.isSubmitting) {
      return;
    }
    this.closed.emit();
  }

  public submitForm(): void {
    if (this.isSubmitting || !this.companyId || !this.salesOrder) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const shipmentDate = (raw.shipmentDate ?? '').trim();
    if (!shipmentDate) {
      return;
    }

    const request: CreateShipmentRequest = {
      shipmentDate: SalesOrderCreateShipmentModalComponent.toIsoDateTime(shipmentDate),
      lines: raw.lines.map((line) => ({
        salesOrderLineId: line.salesOrderLineId ?? '',
        quantity: Number(line.quantity ?? 0),
        unitOfMeasureId: Number(line.unitOfMeasureId ?? 0),
      })),
    };

    this.isSubmitting = true;
    this.errorMessageKey = '';

    this.shipmentService
      .createShipment(this.companyId, this.salesOrder.id, request)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => {
          this.shipmentCreated.emit();
          this.closed.emit();
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorMessageKey = this.mapErrorKey(error);
          return EMPTY;
        }),
        finalize(() => {
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }

  // Map structured backend error codes (Stage A unified the envelope to { code, message } across
  // every sales-order mutation). Keep in sync with TaskManager.Services.SalesOrderErrorCodes.
  private readonly errorCodeKeyMap: Readonly<Record<string, string>> = {
    ORDER_INVALID_STATUS: 'COMPONENTS.SALES_ORDERS.ERRORS.SHIP_INVALID_STATUS',
    ORDER_TERMINAL: 'COMPONENTS.SALES_ORDERS.ERRORS.ORDER_TERMINAL',
    ORDER_NOT_CONFIRMED: 'COMPONENTS.SALES_ORDERS.ERRORS.SHIP_ORDER_NOT_CONFIRMED',
    SHIPMENT_INSUFFICIENT_RESERVATION: 'COMPONENTS.SALES_ORDERS.ERRORS.SHIP_INSUFFICIENT_RESERVATION',
  };

  private mapErrorKey(error: HttpErrorResponse): string {
    if (error.status === 409) {
      return 'COMPONENTS.SALES_ORDERS.ERRORS.STOCK_CONFLICT';
    }
    if (error.status === 400) {
      const code = (error.error as { code?: string } | null)?.code;
      if (code && this.errorCodeKeyMap[code]) {
        return this.errorCodeKeyMap[code];
      }
      const message = SalesOrderCreateShipmentModalComponent.extractMessage(error.error);
      if (message) {
        const lower = message.toLowerCase();
        if (lower.includes('over') || lower.includes('exceed') || lower.includes('shipped')) {
          return 'COMPONENTS.SALES_ORDERS.ERRORS.OVER_SHIP';
        }
        if (lower.includes('status') || lower.includes('confirmed') || lower.includes('partiallyshipped')) {
          return 'COMPONENTS.SALES_ORDERS.ERRORS.SHIP_INVALID_STATUS';
        }
      }
      return 'COMPONENTS.SALES_ORDERS.ERRORS.SHIPMENT_FAILED';
    }
    return 'COMPONENTS.SALES_ORDERS.ERRORS.SHIPMENT_FAILED';
  }

  private resetFormFromOrder(): void {
    this.errorMessageKey = '';
    this.formModel.clearLines();

    const order = this.salesOrder;
    if (!order) {
      this.lineRows = [];
      this.customerLabel = '';
      this.salesOrderNumberLabel = '';
      this.form.controls.shipmentDate.setValue(this.today);
      return;
    }

    // Pre-fill the read-only context strip so the operator immediately sees which order +
    // customer the shipment will be linked to. The link itself is encoded in the URL
    // (`/companies/{companyId}/sales-orders/{salesOrderId}/shipments`) so the backend always
    // attaches the new shipment to the parent sales order.
    // Prefer the resolved human-readable customer name when the parent provided one; fall back
    // to the BC-style `customerNumber` so the field is never blank.
    this.customerLabel = (this.customerName || '').trim() || order.customerNumber || '';
    this.salesOrderNumberLabel = order.number || '';

    const rows: CreateShipmentLineRowView[] = [];
    const serviceItemIds = new Set(
      this.inventoryItemOptions
        .filter((option) => option.itemType === INVENTORY_ITEM_TYPE_SERVICE)
        .map((option) => option.id),
    );
    order.lines.forEach((line) => {
      const isServiceLine =
        !!line.inventoryItemId && serviceItemIds.has(line.inventoryItemId);
      const remaining = SalesOrderCreateShipmentModalComponent.computeRemaining(line);
      // Service lines never block on remaining — the backend bypasses the stock pipeline
      // for them. Skip the row when remaining is zero for Product/Hybrid only.
      if (!isServiceLine && remaining <= 0) {
        return;
      }
      const unitOfMeasureId = this.resolveUnitOfMeasureId(line);
      if (unitOfMeasureId === null) {
        return;
      }

      const unitLabel = this.resolveUnitLabel(line, unitOfMeasureId);
      // Service lines: default the quantity to the full ordered amount and drop the max
      // validator. Product/Hybrid lines keep the legacy remaining-quantity behavior.
      const defaultQuantity = isServiceLine
        ? remaining > 0
          ? remaining
          : line.quantity
        : remaining;
      this.formModel.addLine({
        salesOrderLineId: line.id,
        quantity: defaultQuantity,
        unitOfMeasureId,
        maxQuantity: remaining,
        isServiceLine,
      });

      rows.push({
        index: rows.length,
        description: line.description ?? '',
        description2: line.description2 ?? '',
        unitLabel,
        remaining,
        maxLabel: `${remaining}`,
        isServiceLine,
      });
    });

    this.lineRows = rows;
    this.form.controls.shipmentDate.setValue(this.today);
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private resolveUnitOfMeasureId(line: CompanySalesOrderLine): number | null {
    if (line.unitOfMeasureId !== null && line.unitOfMeasureId !== undefined) {
      return line.unitOfMeasureId;
    }
    return this.unitsOfMeasure[0]?.id ?? null;
  }

  private resolveUnitLabel(line: CompanySalesOrderLine, unitOfMeasureId: number): string {
    if (line.unitOfMeasureDisplayName) {
      return line.unitOfMeasureDisplayName;
    }
    if (line.unitOfMeasureCode) {
      return line.unitOfMeasureCode;
    }
    const lookup = this.unitsOfMeasure.find((unit) => unit.id === unitOfMeasureId);
    return lookup?.displayName ?? lookup?.code ?? '';
  }

  private static computeRemaining(line: CompanySalesOrderLine): number {
    // The backend payload may omit `shippedQuantity` on lines that have no fulfilment
    // yet. Treat undefined/null as zero and fall back to the full line quantity.
    if (line.shippedQuantity === undefined || line.shippedQuantity === null) {
      return Number.isFinite(line.quantity) && line.quantity > 0 ? line.quantity : 0;
    }
    const shipped = Number(line.shippedQuantity);
    const safeShipped = Number.isFinite(shipped) && shipped > 0 ? shipped : 0;
    return Math.max(0, line.quantity - safeShipped);
  }

  private static formatToday(): string {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private static toIsoDateTime(yyyyMmDd: string): string {
    // Backend accepts UTC ISO timestamps; the picker only collects the date portion.
    return `${yyyyMmDd}T00:00:00Z`;
  }

  private static extractMessage(payload: unknown): string | null {
    if (!payload || typeof payload !== 'object') {
      return null;
    }
    const message = (payload as { message?: unknown }).message;
    return typeof message === 'string' ? message : null;
  }
}
